import { openAuthorizeUrl } from "@/utils/linkedaccounts/linkedAccounts";
import { X_MEDIA_UPLOAD_STORAGE_KEY } from "@/utils/linkedaccounts/x";
import { trackLegacyEvent } from "@analytics";
import {
  Body,
  Box,
  ButtCon,
  Button,
  CloudImage,
  FormControl,
  Heading,
  Icon,
  MenuItem,
  Modal,
  Stack,
  TextArea,
} from "@biom3/react";
import { MODAL_HEIGHT, MODAL_WIDTH, appConfig } from "@constants";
import type { Content } from "@prismicio/client";
import type { QuestKey, QuestState } from "@ui-kit/QuestList";
import { isQuestCompletedForDay } from "@ui-kit/QuestList/QuestItem";
import Link from "next/link";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import React from "react";
import { fileToBase64 } from "./LinkedAccountLogin";

const X_LOGO_URL = "/images/quests/x.svg";
const OVERKNIGHTS_LOGO_URL = "/images/quests/ovks-logo.svg";

const SOV_TWEET_PROMPTS = [
  "I've been waiting for another epic AR experience, can't wait to play @SOV_thegame!",
  "Can't wait to dive into @SOV_thegame! The hype is real for this epic adventure!",
  "Thrilled to join the action in @SOV_thegame ! It's gonna be an insane ride!",
];

type XActionConfirmationModalProps = {
  visible: boolean;
  onClose: () => void;
  game: Content.GameDocument;
  quest: QuestState | undefined;
  showConfirmationModal: () => void;
};

const getModalCopy = (
  tweetUsername: string | undefined,
  questKey: QuestKey | undefined,
  gameName: string | undefined,
): { headerText: string; confirmationText: string } => {
  if (!questKey || !gameName) {
    return { headerText: "", confirmationText: "" };
  }

  switch (questKey) {
    case "x-post-tweet-validation":
      return {
        headerText: `Posting to ${tweetUsername}`, // e.g. Posting to @norma_metaland
        confirmationText: "Post on X",
      };
    case "x-like-and-retweet-validation":
      return {
        headerText: "",
        confirmationText: "Like and Repost",
      };
    default:
      return { headerText: "", confirmationText: "" };
  }
};

export function XActionConfirmationModal({
  visible,
  onClose,
  game,
  quest,
  showConfirmationModal,
}: XActionConfirmationModalProps) {
  const [tweetCustomText, setTweetCustomText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [tweetPrompt, setTweetPrompt] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // bespoke tweet prompts for SoV - this should be removed once quest is finished
  useEffect(() => {
    if (
      game.uid === "syndicate-of-vigilantes-game" &&
      quest?.key === "x-post-tweet-validation"
    ) {
      const randomIndex = Math.floor(Math.random() * SOV_TWEET_PROMPTS.length);
      const prompt = SOV_TWEET_PROMPTS[randomIndex];
      setTweetPrompt(prompt);
    } else {
      setTweetPrompt(quest?.tweetText ?? "");
    }
  }, [quest?.key, quest?.tweetText, game.uid]);

  const trackButtonPress = useCallback(
    (control: string) => {
      trackLegacyEvent({
        screen: "XActionConfirmationModal",
        userJourney: "Games",
        action: "Pressed",
        control,
        controlType: "Button",
        extras: {
          ruleId: quest?.ruleId,
          variant: quest?.key,
        },
      });
    },
    [quest?.key, quest?.ruleId],
  );

  const handleClose = useCallback(() => {
    trackButtonPress("Close");
    setTweetCustomText(quest?.tweetDraftText ?? "");
    setError(null);
    setSelectedImage(null);
    setImageError(null);
    onClose();
  }, [onClose, trackButtonPress, quest?.tweetDraftText]);

  const handleImageSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // Validate file type
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
      ];
      if (!allowedTypes.includes(file.type)) {
        setImageError("Please select a valid image file (JPEG, PNG, or WebP).");
        return;
      }

      // Validate file size (max 5MB for X)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        setImageError("Image file size must be less than 5MB.");
        return;
      }

      setSelectedImage(file);
      setImageError(null);
    },
    [],
  );

  const handleRemoveImage = useCallback(() => {
    setSelectedImage(null);
    setImageError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const handlePostOnX = useCallback(async () => {
    if (!quest) return;

    if (
      quest?.key === "x-post-tweet-validation" &&
      tweetCustomText.trim() === ""
    ) {
      setError("The post content cannot be empty.");
      return;
    }

    // Check if image is required but not attached
    if (
      quest?.key === "x-post-tweet-validation" &&
      quest?.tweetRequiresImage &&
      !selectedImage
    ) {
      setImageError("An image is required for this post.");
      return;
    }

    setError(null);
    setImageError(null);
    setIsUploading(true);

    try {
      if (selectedImage && quest?.tweetRequiresImage) {
        const base64Image = await fileToBase64(selectedImage);
        const imageData = {
          base64: base64Image,
          filename: selectedImage.name,
          type: selectedImage.type,
        };
        const key = `${X_MEDIA_UPLOAD_STORAGE_KEY}_${quest.ruleId}`;
        localStorage.setItem(key, JSON.stringify(imageData));
      }

      const tweetContent = `${tweetCustomText} ${tweetPrompt}`;

      localStorage.removeItem(quest.ruleId);
      showConfirmationModal();
      openAuthorizeUrl(
        quest.ruleId,
        quest.key,
        quest.linkUrl || "",
        game.data.name ?? "",
        game.data.games_service_item?.id as string,
        false,
        tweetContent,
      );
      handleClose();
    } catch (error) {
      console.error("Failed to upload image:", error);
      setImageError("Failed to upload image. Please try again.");
    } finally {
      setIsUploading(false);
    }
  }, [
    quest,
    tweetCustomText,
    showConfirmationModal,
    game.data.name,
    game.data.games_service_item?.id,
    handleClose,
    tweetPrompt,
    selectedImage,
  ]);

  const { headerText, confirmationText } = getModalCopy(
    quest?.tweetUsername,
    quest?.key,
    game.data.name as string,
  );

  const isOverKnights = game.uid === "overknights";
  const isSoV = game.uid === "syndicate-of-vigilantes-game";

  const tweetDate = new Date("2025-05-17");
  const recurringQuestCompletedForDay =
    isQuestCompletedForDay(quest) && quest?.recurring;

  useEffect(() => {
    if (visible) {
      setTweetCustomText(quest?.tweetDraftText ?? "");
    }
  }, [visible, quest?.tweetDraftText]);

  const confirmationButtons = useCallback(() => {
    const isRecurringTweetQuestCompleted =
      recurringQuestCompletedForDay && quest?.key === "x-post-tweet-validation";
    const shouldShowPrimaryButton = !isRecurringTweetQuestCompleted;
    const shouldShowGoBackButton =
      isRecurringTweetQuestCompleted ||
      !(isOverKnights || quest?.key === "x-post-tweet-validation");

    const isImageRequiredButMissing =
      quest?.key === "x-post-tweet-validation" &&
      quest?.tweetRequiresImage &&
      !selectedImage;

    const isTextEmpty =
      quest?.key === "x-post-tweet-validation" && tweetCustomText.trim() === "";

    const isButtonDisabled =
      isImageRequiredButMissing || isTextEmpty || isUploading;

    return (
      <>
        {shouldShowPrimaryButton && (
          <Button
            variant="primary"
            onClick={handlePostOnX}
            disabled={isButtonDisabled}
            sx={{ width: "100%", my: "base.spacing.x2" }}
            size="large"
          >
            {isUploading ? "Uploading..." : confirmationText}
          </Button>
        )}
        {shouldShowGoBackButton && (
          <Button
            variant="tertiary"
            onClick={handleClose}
            sx={{ width: "100%" }}
            size="large"
          >
            Go back
          </Button>
        )}
      </>
    );
  }, [
    recurringQuestCompletedForDay,
    quest?.key,
    isOverKnights,
    confirmationText,
    handlePostOnX,
    handleClose,
    selectedImage,
    tweetCustomText,
    isUploading,
    quest?.tweetRequiresImage,
  ]);

  const textAreaMargin = useMemo(() => {
    if (isOverKnights) return "base.spacing.x6";
    if (isSoV) return "base.spacing.x16";

    return "base.spacing.x12";
  }, [isOverKnights, isSoV]);

  return (
    <Modal
      visible={visible}
      onCloseModal={onClose}
      showBgOverlay={false}
      outsideClicksClose={false}
    >
      <Modal.Content
        sx={{
          d: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "space-between",
          borderRadius: "base.borderRadius.x10",
          position: "relative",
          overflow: "hidden",
          background: "base.color.brand.2",
          backgroundImage: `url("${appConfig.ASSET_BASE_URL}/images/quests/follow-confirmation-background.svg")`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "top",
          height: quest?.tweetRequiresImage ? "720px" : MODAL_HEIGHT,
          width: {
            default: "calc(100dvw - 40px)",
            small: MODAL_WIDTH,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: quest?.tweetCaption ? "base.spacing.x1" : "base.spacing.x4",
            width: "100%",
          }}
        >
          <CloudImage
            imageUrl={`${appConfig.ASSET_BASE_URL}${X_LOGO_URL}`}
            relativeImageSizeInLayout="64px"
            sx={{
              mt: quest?.tweetRequiresImage
                ? "base.spacing.x18"
                : "base.spacing.x32",
              mb: "base.spacing.x8",
              width: "64px",
            }}
          />

          {headerText !== "" && (
            <Heading
              size="medium"
              weight="bold"
              sx={{ mx: "base.spacing.x12", textAlign: "center" }}
            >
              {headerText}
            </Heading>
          )}
          <Body
            sx={{
              px: "base.spacing.x10",
              color: "base.color.text.body.secondary",
              textAlign: "center",
              fontSize: "base.text.body.small.regular.fontSize",
            }}
          >
            {quest?.tweetCaption ?? ""}
          </Body>
          {quest?.key === "x-post-tweet-validation" &&
            !recurringQuestCompletedForDay && (
              <FormControl>
                <TextArea
                  sizeVariant={quest?.tweetCaption ? "medium" : "large"}
                  validationStatus={error ? "error" : undefined}
                  value={tweetCustomText}
                  hideClearValueButton={false}
                  onClearValue={() => setTweetCustomText("")}
                  onChange={(event) => {
                    setTweetCustomText(event.target.value);
                    if (error) setError(null);
                  }}
                  placeholder={quest?.tweetPlaceholder ?? ""}
                  sx={{
                    minWidth: "296px",
                    mt: "base.spacing.x3",
                    color: "base.color.text.body.secondary",
                    position: "relative",

                    /* render the prefix text before the textarea */
                    "&::before": {
                      content: `"${tweetPrompt}"`,
                      position: "absolute",
                      fontFamily: "base.font.family.body.primary",
                      whiteSpace: "pre-wrap",
                      maxWidth: "250px",
                    },

                    "& textarea": {
                      resize: "none",
                      mt: textAreaMargin,
                      color: "base.color.text.body.secondary",
                      lineHeight: "base.spacing.x5",
                    },
                    "& button": {
                      mt: isOverKnights
                        ? "base.spacing.x6"
                        : "base.spacing.x12",
                    },
                  }}
                />
                {error && (
                  <FormControl.Validation validationStatus="error">
                    {error}
                  </FormControl.Validation>
                )}
              </FormControl>
            )}

          {/* Image Attachment Section */}
          {quest?.key === "x-post-tweet-validation" &&
            !recurringQuestCompletedForDay &&
            quest?.tweetRequiresImage && (
              <Stack
                sx={{
                  width: "100%",
                  alignItems: "center",
                  gap: "base.spacing.x3",
                }}
              >
                {/* File Input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={handleImageSelect}
                  style={{ display: "none" }}
                />

                {/* Image Selection Button - Only show when no image is selected */}
                {!selectedImage && (
                  <Button
                    variant="tertiary"
                    size="medium"
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                      width: "296px",
                      border: "1px dashed",
                      borderColor: "base.color.translucent.standard.400",
                      color: "base.color.text.body.secondary",
                      "&:hover": {
                        borderColor: "base.color.accent.2",
                        color: "base.color.accent.2",
                      },
                    }}
                  >
                    <Icon icon="Add" sx={{ mr: "base.spacing.x2" }} />
                    Add Image
                  </Button>
                )}
                {selectedImage && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "base.spacing.x3",
                      width: "296px",
                      padding: "base.spacing.x3",
                      borderRadius: "base.borderRadius.x4",
                      border: "1px solid",
                      borderColor: "base.color.translucent.standard.400",
                      backgroundColor: "base.color.translucent.standard.100",
                    }}
                  >
                    <Box
                      sx={{
                        width: "40px",
                        height: "40px",
                        borderRadius: "base.borderRadius.x2",
                        overflow: "hidden",
                        flexShrink: 0,
                      }}
                    >
                      <img
                        src={URL.createObjectURL(selectedImage)}
                        alt="Selected"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: "0", overflow: "hidden" }}>
                      <Body
                        size="small"
                        sx={{
                          color: "base.color.text.body.primary",
                          fontWeight: "base.text.body.small.bold.fontWeight",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          display: "block",
                        }}
                      >
                        {selectedImage.name}
                      </Body>
                      <Body
                        size="xSmall"
                        sx={{
                          color: "base.color.text.body.secondary",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          display: "block",
                        }}
                      >
                        Image attached
                      </Body>
                    </Box>
                    <ButtCon
                      icon="Close"
                      variant="tertiary"
                      onClick={handleRemoveImage}
                      sx={{
                        flexShrink: 0,
                        width: "24px",
                        height: "24px",
                        ml: "base.spacing.x2",
                        "&:hover": {
                          backgroundColor:
                            "base.color.translucent.standard.300",
                        },
                      }}
                    />
                  </Box>
                )}
                {imageError && (
                  <Body
                    size="small"
                    sx={{
                      color: "base.color.status.fatal.bright",
                      textAlign: "center",
                      maxWidth: "296px",
                    }}
                  >
                    {imageError}
                  </Body>
                )}
                {!selectedImage && (
                  <Body
                    size="xSmall"
                    sx={{
                      color: "base.color.text.body.secondary",
                      textAlign: "center",
                      maxWidth: "296px",
                    }}
                  >
                    Supported formats: JPEG, PNG, WebP (max 5MB)
                  </Body>
                )}
              </Stack>
            )}

          {quest?.key === "x-post-tweet-validation" &&
            recurringQuestCompletedForDay && (
              <Body
                size="small"
                weight="regular"
                sx={{
                  textAlign: "center",
                  color: "base.color.text.body.secondary",
                  mx: "base.spacing.x4",
                }}
              >
                You've already done this for today! Check back later to complete
                it again.
              </Body>
            )}

          {isOverKnights && quest?.key === "x-like-and-retweet-validation" && (
            <Stack
              sx={{ width: "85%", alignItems: "left", gap: "base.spacing.x0" }}
            >
              <MenuItem size="small">
                <MenuItem.FramedImage
                  imageUrl={`${appConfig.ASSET_BASE_URL}${OVERKNIGHTS_LOGO_URL}`}
                  circularFrame={true}
                />
                <MenuItem.Label>
                  OverKnights
                  <Icon
                    icon="Authenticated"
                    variant="bold"
                    sx={{
                      alignSelf: "center",
                      ml: "base.spacing.x1",
                    }}
                  />
                </MenuItem.Label>
                <MenuItem.Caption>
                  @OverKnightsGame ·{" "}
                  {tweetDate.toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "long",
                  })}
                </MenuItem.Caption>
              </MenuItem>
              <Body
                size="small"
                weight="regular"
                sx={{
                  textAlign: "left",
                  color: "base.color.text.body.secondary",
                  mx: "base.spacing.x4",
                }}
              >
                The Libra Tower Awaits!
                <br />
                <br />
                Pilot your OverKnight.
                <br />
                <br />
                Duel, Ascend and Claim your Destiny!
                <br />
                <br />
                Your mission begins here:
                <Link
                  href="https://play.immutable.com/games/overknights/"
                  target="_blank"
                >
                  play.immutable.com/games/overknights/
                </Link>
              </Body>
              <Stack
                direction="row"
                sx={{
                  height: "70%",
                  gap: "base.spacing.x1",
                  ml: "base.spacing.x2",
                }}
              >
                <Body
                  size="xSmall"
                  sx={{
                    textAlign: "left",
                    ml: "base.spacing.x2",
                    pt: "base.spacing.x2",
                  }}
                >
                  <Icon
                    icon="SupportChat"
                    variant="bold"
                    sx={{
                      width: "11px",
                      height: "11px",
                      position: "relative",
                      bottom: "-1px",
                      mr: "base.spacing.x1",
                      ml: "base.spacing.x1",
                    }}
                  />
                  162
                </Body>
                <Body
                  size="xSmall"
                  weight="regular"
                  sx={{
                    textAlign: "left",
                    ml: "base.spacing.x2",
                    pt: "base.spacing.x2",
                  }}
                >
                  <Icon
                    icon="Exchange"
                    sx={{
                      width: "11px",
                      height: "11px",
                      position: "relative",
                      bottom: "-1px",
                      mr: "base.spacing.x1",
                      ml: "base.spacing.x1",
                    }}
                  />
                  333
                </Body>
                <Body
                  size="xSmall"
                  weight="regular"
                  sx={{
                    textAlign: "left",
                    ml: "base.spacing.x2",
                    pt: "base.spacing.x2",
                  }}
                >
                  <Icon
                    icon="Favourite"
                    variant="bold"
                    sx={{
                      width: "11px",
                      height: "11px",
                      position: "relative",
                      bottom: "-1px",
                      mr: "base.spacing.x1",
                      ml: "base.spacing.x1",
                    }}
                  />
                  410
                </Body>
              </Stack>
            </Stack>
          )}
          {!isOverKnights && quest?.XPostData && (
            <Stack
              sx={{ width: "85%", alignItems: "left", gap: "base.spacing.x0" }}
            >
              <MenuItem size="small">
                {quest?.XPostData.image && (
                  <MenuItem.FramedImage
                    imageUrl={quest?.XPostData.image}
                    circularFrame={true}
                  />
                )}
                <MenuItem.Label>
                  {quest?.XPostData.name || "X User"}
                  <Icon
                    icon="Authenticated"
                    variant="bold"
                    sx={{
                      alignSelf: "center",
                      ml: "base.spacing.x1",
                    }}
                  />
                </MenuItem.Label>
                <MenuItem.Caption>
                  {quest?.XPostData.handle ? `@${quest?.XPostData.handle}` : ""}
                  {quest?.XPostData.handle && quest?.XPostData.postedTime
                    ? " · "
                    : ""}
                  {quest?.XPostData.postedTime &&
                    new Date(quest?.XPostData.postedTime).toLocaleDateString(
                      "en-GB",
                      {
                        day: "numeric",
                        month: "long",
                      },
                    )}
                </MenuItem.Caption>
              </MenuItem>
              {quest?.XPostData.postBody && (
                <Body
                  size="small"
                  weight="regular"
                  sx={{
                    textAlign: "left",
                    color: "base.color.text.body.secondary",
                    mx: "base.spacing.x4",
                    width: "100%",
                  }}
                >
                  {quest?.XPostData.postBody.map((line, i) => {
                    return (
                      <React.Fragment key={line}>
                        {line}{" "}
                        {i < (quest?.XPostData?.postBody?.length ?? 1) - 1 && (
                          <>
                            <br /> <br />
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                </Body>
              )}
              <Stack
                direction="row"
                sx={{
                  height: "70%",
                  gap: "base.spacing.x1",
                  ml: "base.spacing.x2",
                }}
              >
                <Body
                  size="xSmall"
                  sx={{
                    textAlign: "left",
                    ml: "base.spacing.x2",
                    pt: "base.spacing.x2",
                  }}
                >
                  <Icon
                    icon="SupportChat"
                    variant="bold"
                    sx={{
                      width: "11px",
                      height: "11px",
                      position: "relative",
                      bottom: "-1px",
                      mr: "base.spacing.x1",
                      ml: "base.spacing.x1",
                    }}
                  />
                  {quest?.XPostData.commentCount ?? 0}
                </Body>
                <Body
                  size="xSmall"
                  weight="regular"
                  sx={{
                    textAlign: "left",
                    ml: "base.spacing.x2",
                    pt: "base.spacing.x2",
                  }}
                >
                  <Icon
                    icon="Exchange"
                    sx={{
                      width: "11px",
                      height: "11px",
                      position: "relative",
                      bottom: "-1px",
                      mr: "base.spacing.x1",
                      ml: "base.spacing.x1",
                    }}
                  />
                  {quest?.XPostData.retweetCount ?? 0}
                </Body>
                <Body
                  size="xSmall"
                  weight="regular"
                  sx={{
                    textAlign: "left",
                    ml: "base.spacing.x2",
                    pt: "base.spacing.x2",
                  }}
                >
                  <Icon
                    icon="Favourite"
                    variant="bold"
                    sx={{
                      width: "11px",
                      height: "11px",
                      position: "relative",
                      bottom: "-1px",
                      mr: "base.spacing.x1",
                      ml: "base.spacing.x1",
                    }}
                  />
                  {quest?.XPostData.likeCount ?? 0}
                </Body>
              </Stack>
            </Stack>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            p: "base.spacing.x4",
            mt: "auto",
          }}
        >
          {confirmationButtons()}
        </Box>

        <ButtCon
          icon="Close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: "base.spacing.x4",
            right: "base.spacing.x4",
          }}
          variant="tertiary"
        />
      </Modal.Content>
    </Modal>
  );
}
